import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Account as AccountType } from '@ambire-common/interfaces/account'
import { isWeb } from '@common/config/env'
import useAccountsList from '@common/hooks/useAccountsList'
import useController from '@common/hooks/useController'
import useNavigation from '@common/hooks/useNavigation'
import useRoute from '@common/hooks/useRoute'
import { ROUTES } from '@common/modules/router/constants/common'

const extractTriggerAddAccountSheetParam = (search: string | undefined): boolean | null => {
  if (!search) return null

  const params = new URLSearchParams(search)
  const addAccount = params.get('triggerAddAccountBottomSheet')

  // Remove the addAccount parameter
  if (addAccount) {
    params.delete('triggerAddAccountBottomSheet')
    const updatedSearch = params.toString()

    if (isWeb) {
      // Updated URL back into the app, handle it here.
      window.history.replaceState(null, '', `?${updatedSearch}`)
    }

    return addAccount === 'true'
  }

  return null
}

type Options = {
  // Called once the newly picked account becomes the selected account. The
  // route-based screens leave it unset and fall back to navigating to the
  // dashboard; the mobile-web modal passes its close handler so picking an
  // account just dismisses the popup (the dashboard is already mounted behind).
  onAccountSwitched?: () => void
}

// Shared logic for the native and the mobile-web (.web.tsx) renderings of the
// account-select screen, so the two layouts never drift on a window resize.
const useAccountSelectScreen = ({ onAccountSwitched }: Options = {}) => {
  const flatlistRef = useRef(null)
  const { accounts, control, keyExtractor, getItemLayout, shouldDisplayAccounts } = useAccountsList(
    { flatlistRef: flatlistRef as any }
  )
  const { search: routeParams, state: routeState } = useRoute()
  const { navigate, goBack } = useNavigation()
  // Latched on mount, like the extension screen: came straight from the dashboard
  // (pushed on top of it) → pop back to that entry after the switch.
  const cameFromDashboardRef = useRef(
    (routeState as any)?.prevRoute?.pathname?.replace(/^\//, '') === ROUTES.dashboard
  )
  const {
    state: { account }
  } = useController('SelectedAccountController')
  const [pendingToBeSetSelectedAccount, setPendingToBeSetSelectedAccount] = useState('')

  const shouldTriggerAddAccountSheetFromSearch = useMemo(
    () => extractTriggerAddAccountSheetParam(routeParams),
    [routeParams]
  )

  const onAccountSelect = useCallback(
    (addr: AccountType['addr']) => setPendingToBeSetSelectedAccount(addr),
    []
  )

  useEffect(() => {
    // Navigate to the dashboard after the account is selected to avoid showing the dashboard
    // of the previously selected account.
    if (!account || !pendingToBeSetSelectedAccount) return

    if (account.addr === pendingToBeSetSelectedAccount) {
      // Reset the staged selection once it lands, otherwise the condition above
      // stays permanently true. On mobile-web the modal (and this hook) stay
      // mounted after closing, so a still-matching pending value would re-fire
      // onAccountSwitched() on every re-render and instantly close the switcher
      // the next time it's opened — making it look frozen after the first switch.
      setPendingToBeSetSelectedAccount('')
      // Never PUSH the dashboard: that leaves this screen behind it in the
      // history, so Back after a switch reopens the account list (Trello #552).
      if (onAccountSwitched) onAccountSwitched()
      else if (cameFromDashboardRef.current) goBack()
      else navigate(ROUTES.dashboard, { replace: true })
    }
  }, [account, navigate, goBack, onAccountSwitched, pendingToBeSetSelectedAccount])

  return {
    flatlistRef,
    accounts,
    control,
    keyExtractor,
    getItemLayout,
    shouldDisplayAccounts,
    pendingToBeSetSelectedAccount,
    onAccountSelect,
    shouldTriggerAddAccountSheetFromSearch
  }
}

export default useAccountSelectScreen
